import { Component, DestroyRef, LOCALE_ID, OnInit, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ProductService } from '../../../../services/product/product.service';
import { ProductVTO } from '../../../../pages/admin-dashboard/products/product.model';
import { ProductsFileMgt } from '../../../../services/product/file-management/products.file-mgt.service';
import { SafeUrl } from '@angular/platform-browser';
import { catchError, finalize, forkJoin, map, of } from 'rxjs';
import { CategoryService } from '../../../../services/category/category.service';
import { CategoryListItem, CategoryResultSet } from '../../../../pages/admin-dashboard/categories/category.model';
import { ProductsLookupService } from '../../../../services/product/lookup.service';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import {
  arabicCharactersValidator,
  barcodeValidator,
  englishCharactersValidator,
  numericOnlyValidator,
} from './utils/validation';
import { ThousandSeparatorDirective } from '../../../admin-customer-shared-components/create-pharmacy-component/pharmacy-quota/utils/separator.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Loading } from '../../../../utils/loading/loading';
import { Router } from '@angular/router';
import { Error } from '../../../public-user-component/auth/shared/error/error';
import { ConfirmationCancel } from './utils/confirmation-cancel/confirmation-cancel';
import { Notification } from '../../../shared/notification/notification';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { EditAttribute } from '../../attributes/edit-attribute/edit-attribute';
import { EditAttributes } from './utils/edit-attributes/edit-attributes';
import { NoDataComponent } from '../../../shared/no-data-component/no-data-component';
import { EmptyData } from '../../../../utils/empty-data/empty-data';

@Component({
  selector: 'app-edit-product-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ThousandSeparatorDirective,
    Error,
    ConfirmationCancel,
    SuccessfulNotification,
    EditAttributes,
    EmptyData,
  ],
  templateUrl: './edit-product-form.html',
  styleUrl: './edit-product-form.scss',
})
export class EditProductForm implements OnInit {
  private router = inject(Router);
  private productService = inject(ProductService);
  private fileMgt = inject(ProductsFileMgt);
  private locale = inject(LOCALE_ID);
  private lookupService = inject(ProductsLookupService);
  productImageUrl = signal<{ url: SafeUrl | string; isImage: boolean } | undefined>(undefined);
  subProductId = input<number | null>();
  productImages = signal<{ fid: string; url: SafeUrl | string; isImage: boolean; loading: boolean; error: boolean }[]>(
    [],
  );
  private destroyRef = inject(DestroyRef);
  deletingImage = signal<boolean>(false);
  errorDeletingImage = signal<string>('');

  productData = signal<ProductVTO | null>(null);
  loadingProduct = signal<boolean>(false);
  errorLoadingProduct = signal<string>('');
  productType = input<'has-sub' | 'has-no-sub' | 'sub-product' | null>();
  productId = input<number | null>(null);
  loading = signal<boolean>(false);
  formSubmitted = false;
  isDragging = false;
  categories: CategoryListItem[] = [];
  subCategories: LookupVTO[] = [];
  mfgCompanies: LookupVTO[] = [];
  selectedImages = signal<{ file: File; preview: string }[]>([]);
  imageValidation = signal<boolean>(false);
  validationSize = signal<boolean>(false);
  selectedImage = signal<File | null>(null);
  imagePreview = signal('');
  errorUpdaingProduct = signal('');
  confirmationCancel = signal<boolean>(false);
  successUpdate = signal<boolean>(false);
  parentProductAttributes = signal<any>('');
  selectedAttributeValueIds = signal<number[]>([]);
  productForm = new FormGroup({
    englishName: new FormControl('', { validators: [Validators.maxLength(255), englishCharactersValidator()] }),
    arabicName: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(255), arabicCharactersValidator()],
      updateOn: 'blur',
    }),
    descriptionEn: new FormControl('', [Validators.maxLength(2000), englishCharactersValidator()]),
    descriptionAr: new FormControl('', [Validators.maxLength(2000), arabicCharactersValidator()]),
    category: new FormControl('', []),
    subCategory: new FormControl(''),
    mfgCompany: new FormControl(''),
    peoplePrice: new FormControl<number | null>(null, {
      validators: [Validators.min(1), Validators.max(1_000_000)],
    }),
    sellerDiscount: new FormControl<number | null>(null, {
      validators: [Validators.min(0), Validators.max(100), numericOnlyValidator()],
    }),
    sellerPrice: new FormControl<number | null>(null),
    pharmacyDiscount: new FormControl<number | null>(null, {
      validators: [Validators.min(0), Validators.max(100), numericOnlyValidator()],
    }),
    pharmacyPrice: new FormControl<number | null>(null),
    // attributes: new FormArray([]),
    // subProducts: new FormArray([]),
    barcode: new FormControl<string | null>(null, {
      validators: [barcodeValidator()],
    }),
    imagesFids: new FormControl<string[]>([]),
    newImagesFids: new FormControl<string[]>([]),
    attributes: new FormControl<[]>([]),
  });

  subProductExcludedFields = signal(['englishName', 'arabicName', 'mfgCompany']);
  productExcludedFields = signal(['attributes']);

  constructor() {
    effect(() => {
      if (this.productType() === 'sub-product') {
        this.enableFields(this.productExcludedFields()); // re-enable product-only fields
        this.disableFields(this.subProductExcludedFields()); // disable sub-product excluded
      } else {
        this.enableFields(this.subProductExcludedFields()); // re-enable sub-product excluded
        this.disableFields(this.productExcludedFields()); // disable product-only fields
      }
    });
  }

  private readonly validatorsMap: Partial<Record<keyof typeof this.productForm.controls, ValidatorFn[]>> = {
    englishName: [Validators.maxLength(255), englishCharactersValidator()],
    arabicName: [Validators.required, Validators.maxLength(255), arabicCharactersValidator()],
    mfgCompany: [Validators.required],
    // add attributes validators here if any
  };

  private disableFields(fields: string[]) {
    fields.forEach((field) => {
      const control = this.productForm.get(field);
      control?.clearValidators();
      control?.disable();
      control?.updateValueAndValidity();
    });
  }

  private enableFields(fields: string[]) {
    fields.forEach((field) => {
      const control = this.productForm.get(field);
      control?.enable();
      this.restoreValidators(field);
      control?.updateValueAndValidity();
    });
  }

  private restoreValidators(field: string) {
    const validators = this.validatorsMap[field as keyof typeof this.validatorsMap];
    const control = this.productForm.get(field);
    if (validators && control) {
      control.setValidators(validators);
    }
  }

  loadProduct(id: number) {
    this.loadingProduct.set(true);
    this.productService
      .getAdminProductById(id)
      .pipe(
        finalize(() => {
          this.loadingProduct.set(false);
        }),
      )
      .subscribe({
        next: (product) => {
          this.productForm.patchValue({
            englishName: product?.titleEn || '',
            arabicName: product?.titleAr || '',
            descriptionEn: product?.descriptionEn || '',
            descriptionAr: product?.descriptionAr || '',
            category: this.productType() === 'has-sub' ? null : product?.category?.id.toString() || '',
            subCategory: this.productType() === 'has-sub' ? null : product.subCategory?.id.toString() || '',
            mfgCompany: product.mfgCompany?.id.toString() || '',
            peoplePrice: this.productType() === 'has-sub' ? null : product.customerPrice,
            sellerDiscount: this.productType() === 'has-sub' ? null : product.sellerPriceDiscountPercentage,
            pharmacyDiscount: this.productType() === 'has-sub' ? null : product.businessPriceDiscountPercentage,
            barcode: this.productType() === 'has-sub' ? null : product.barcode,
            imagesFids: product.imagesFids,
          });

          this.productData.set(product);

          if (!this.productData()?.profileImageFid) {
            this.productService.getAllAdminProducts({}, this.isArabic ? 'ar' : 'en').subscribe((res) => {
              const found = res.list?.find((p) => p.id === id);
              if (found?.profileImageFid) {
                this.fetchImage(found.profileImageFid).subscribe({
                  next: (data) => {
                    this.productImageUrl.set(data);
                  },
                  error: () => {
                    this.productImageUrl.set({ url: '', isImage: false });
                  },
                });
              } else {
                this.productImageUrl.set({ url: '', isImage: false });
              }
            });
          } else {
            this.fetchImage(this.productData()?.profileImageFid!).subscribe({
              next: (data) => {
                this.productImageUrl.set(data);
              },
              error: () => {
                this.productImageUrl.set({ url: '', isImage: false });
              },
            });

            if (product.imagesFids && product.imagesFids.length > 0) {
              const extraFids = product.imagesFids.slice(1);
              if (extraFids.length === 0) return;

              const imageRequests = extraFids.map((fid: any) => ({
                fid,
                url: '',
                isImage: false,
                loading: true,
                error: false,
              }));

              this.productImages.set(imageRequests);

              extraFids.forEach((fid) => {
                this.fetchImage(fid).subscribe({
                  next: (data) => {
                    this.productImages.update((images) =>
                      images.map((img) =>
                        img.fid === fid ? { ...img, url: data.url, isImage: data.isImage, loading: false } : img,
                      ),
                    );
                  },
                  error: () => {
                    this.productImages.update((images) =>
                      images
                        .map((img) => (img.fid === fid ? { ...img, loading: false, error: true } : img))
                        .filter((img) => !img.error),
                    );
                  },
                });
              });
            }
          }
          this.loadingProduct.set(false);

          this.loadCategories();
          this.loadMfgCompanies();
        },
        error: (err) => {
          this.productImageUrl.set({ url: '', isImage: false });
          this.errorLoadingProduct.set(
            this.isArabic ? err.error.messageAr || 'حدث بعض الخطأ' : err.error.messageEn || 'some error occured!',
          );
        },
      });
  }

  loadSubProduct(id: number, subProductId: number) {
    this.loadingProduct.set(true);
    this.productService
      .getSubProductById(id, subProductId)
      .pipe(
        finalize(() => {
          this.loadingProduct.set(false);
        }),
      )
      .subscribe({
        next: (product) => {
          this.productForm.patchValue({
            englishName: product?.titleEn || '',
            arabicName: product?.titleAr || '',
            descriptionEn: product?.descriptionEn || '',
            descriptionAr: product?.descriptionAr || '',
            category: product?.category?.id.toString() || '',
            subCategory: this.isArabic ? product.subCategory?.titleAr : product.subCategory?.titleEn,
            mfgCompany: product.mfgCompany?.id.toString() || '',
            peoplePrice: product.customerPrice,
            sellerDiscount: product.sellerPriceDiscountPercentage,
            pharmacyDiscount: product.businessPriceDiscountPercentage,
            barcode: product.barcode,
          });

          this.productData.set(product);

          if (!this.productData()?.profileImageFid) {
            this.productService.getAllAdminProducts({}, this.isArabic ? 'ar' : 'en').subscribe((res) => {
              const found = res.list?.find((p) => p.id === id);
              if (found?.profileImageFid) {
                this.fetchImage(found.profileImageFid).subscribe({
                  next: (data) => {
                    this.productImageUrl.set(data);
                  },
                  error: () => {
                    this.productImageUrl.set({ url: '', isImage: false });
                  },
                });
              } else {
                this.productImageUrl.set({ url: '', isImage: false });
              }
            });
          } else {
            this.fetchImage(this.productData()?.profileImageFid!).subscribe({
              next: (data) => {
                this.productImageUrl.set(data);
              },
              error: () => {
                this.productImageUrl.set({ url: '', isImage: false });
              },
            });

            if (product.imagesFids?.length! > 1) {
              const imageRequests = product.imagesFids!.slice(1).map((fid: string) => this.fetchImage(fid));

              forkJoin(imageRequests).subscribe({
                next: (images: any) => this.productImages.set(images),
                error: () => this.productImages.set([]),
              });
            }
          }
          this.loadingProduct.set(false);

          this.loadCategories();
          this.loadMfgCompanies();
        },
        error: (err) => {
          this.productImageUrl.set({ url: '', isImage: false });
          this.errorLoadingProduct.set(
            this.isArabic ? err.error.messageAr || 'حدث بعض الخطأ' : err.error.messageEn || 'some error occured!',
          );
        },
      });
  }

  getSubProductAttribute(productId: number, subProductId: number) {
    this.productService.getSubProductAttributes(productId, subProductId).subscribe({
      next: (data: any) => {
        this.productForm.patchValue({
          attributes: data.list,
        });
        const existingValueIds = data.list.map((attr: any) => attr.attributeValue?.id).filter(Boolean);
        this.selectedAttributeValueIds.set(existingValueIds);
      },
      error: () => {},
    });
  }

  getProductAttribute(productId: number) {
    this.productService.getProductAttributes(productId).subscribe({
      next: (data) => {
        this.parentProductAttributes.set(data.list);
      },
      error: () => {},
    });
  }

  loadCategories() {
    this.lookupService.getLookupCategories().subscribe({
      next: (data) => {
        this.categories = data.list;
        this.loadSubCategories();
      },
    });
  }

  loadSubCategories() {
    const categoryId = this.productForm.get('category')?.value;
    if (!categoryId) return; // guard if no category selected

    this.lookupService.getLookupSubCategories(+categoryId).subscribe({
      next: (data) => {
        this.subCategories = data.list;
        const subCategoryId = this.productData()?.subCategory?.id;
        if (subCategoryId) {
          this.productForm.patchValue({
            subCategory: subCategoryId.toString(),
          });
        }
      },
    });
  }

  loadMfgCompanies() {
    this.lookupService.getLookupMFGCompanies().subscribe({
      next: (data) => {
        this.mfgCompanies = data.list;
      },
    });
  }

  onAttributeChange(attributeKeyId: number, attributeValueId: number) {
    const attrs = this.parentProductAttributes();
    const keyIndex = attrs.findIndex((a: any) => a.attributeKey.id === attributeKeyId);

    this.selectedAttributeValueIds.update((prev) => {
      const next = [...prev];
      next[keyIndex] = +attributeValueId;
      return next;
    });
  }
  private fetchImage(fid: string) {
    return this.fileMgt.getImageUrl(fid);
  }
  ngOnInit() {
    const id = this.productId();
    const subProductId = this.subProductId();

    if (this.productType() === 'sub-product') {
      this.loadSubProduct(id!, subProductId!);
      this.getSubProductAttribute(id!, subProductId!);
      this.getProductAttribute(id!);
    } else {
      this.loadProduct(id!);
    }

    this.productForm
      .get('peoplePrice')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calcSellerPrice();
        this.calcPharmacyPrice();
      });

    this.productForm
      .get('sellerDiscount')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.calcSellerPrice());

    this.productForm
      .get('pharmacyDiscount')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.calcPharmacyPrice());
  }

  processFile(file: File) {
    if (!file.type.match(/image\/(png|jpe?g)|application\/pdf/)) {
      this.imageValidation.set(true);
      // alert($localize`:@@companyFormFileTypeError:Only PNG and JPG files are allowed`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      this.validationSize.set(true);
      // alert($localize`:@@companyFormFileSizeError:File ${file.name} exceeds 5MB limit`);
      return;
    }

    this.selectedImage.set(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      this.selectedImages.update((imgs) => [...imgs, { file, preview: e.target?.result as string }]);
    };
    reader.readAsDataURL(file);
    // input.value = '';
  }

  onFileSelect(event: Event) {
    this.imageValidation.set(false);
    this.validationSize.set(false);
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // validate first
    if (!file.type.match(/image\/(png|jpe?g)/)) {
      this.imageValidation.set(true);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.validationSize.set(true);
      return;
    }

    // just preview, don't upload yet
    const reader = new FileReader();
    reader.onload = (e) => {
      this.selectedImages.update((imgs) => [...imgs, { file, preview: e.target?.result as string }]);
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  onUploadFile() {
    if (this.imageValidation() || this.validationSize()) return;
    this.fileMgt.uploadImage(this.selectedImage()!).subscribe({
      next: (res) => {
        const currentFids: string[] = this.productForm.get('newImagesFids')?.value ?? [];

        this.productForm.patchValue({
          newImagesFids: [...currentFids, res.fid],
        });
        // this.uploadedFid.set(res.fid);
        // this.uploadSuccess.emit(res.fid);
      },
      error: (err) => {
        // this.errorUploading.set(
        //   this.isArabic()
        //     ? err.messageAr || 'حدث خطأ أثناء الرفع!'
        //     : err.messageEn || 'some error occured while uploading!',
        // );
      },
    });
  }

  // setPrimaryImage(index: number) {
  //   this.selectedImages.forEach((img, i) => {
  //     img.isPrimary = i == index;
  //   });
  // }

  removeSelectedImage(index: number) {
    this.selectedImages.update((imgs) => imgs.filter((_, i) => i !== index));
  }

  updateSelectedImages(imageFid: string) {
    const images = this.productForm.get('imagesFids')?.value;
    const updatedImages = images?.filter((fid) => fid !== imageFid);
    this.productForm.patchValue({
      imagesFids: updatedImages,
    });

    this.productImages.update((imgs) => imgs.filter((img) => img.fid !== imageFid));
  }

  onSubmit() {
    // console.log('starting form...');
    this.formSubmitted = true;
    if (this.productForm.invalid) {
      Object.keys(this.productForm.controls).forEach((key) => {
        this.productForm.get(key)?.markAsTouched();
      });
      console.log('invalid form...', this.productForm);
      return;
    }

    // console.log('updating product...');
    this.loading.set(true);
    this.errorUpdaingProduct.set('');

    if (this.selectedImages().length === 0) {
      this.submitForm();
      return;
    }

    const uploads$ = this.selectedImages().map((img) =>
      this.fileMgt.uploadImage(img.file).pipe(
        map((res) => ({ success: true, fid: res.fid })),
        catchError(() => of({ success: false, fid: null })),
      ),
    );

    forkJoin(uploads$).subscribe({
      next: (results) => {
        const failedCount = results.filter((r) => !r.success).length;

        if (failedCount > 0) {
          this.errorUpdaingProduct.set(
            this.isArabic
              ? `فشل تحميل ${failedCount} صورة. يرجى المحاولة مجدداً.`
              : `${failedCount} image(s) failed to upload. Please try again.`,
          );
          this.loading.set(false);
          return;
        }

        const fids = results.map((r) => r.fid).filter(Boolean) as string[];
        this.productForm.patchValue({ newImagesFids: fids });
        this.submitForm();
      },
      error: (err) => {
        console.log('err:', err);
        // this.errorUpdaingProduct.set(
        //   this.isArabic
        //     ? err?.messageAr || err.messageEn || 'حدث بعض الخطأ!'
        //     : err?.messageEn || err.messageAr || 'Some error occurred!',
        // );
      },
    });
  }

  private submitForm() {
    const payload = { id: this.productId(), subId: this.subProductId(), ...this.productForm.getRawValue() };
    const enhancePayload = {
      descriptionAr: payload?.descriptionAr || null,
      descriptionEn: payload?.descriptionEn || null,
      categoryId: payload?.category,
      subCategoryId: payload?.subCategory,
      barcode: payload?.barcode,
      customerPrice: payload.peoplePrice,
      sellerPriceDiscountPercentage: payload.sellerDiscount,
      businessPriceDiscountPercentage: payload.pharmacyDiscount,
      imagesFids: [
        ...new Set(
          [
            ...(payload.imagesFids ?? []), // ✅ reflects deletions
            ...(payload.newImagesFids ?? []), // ✅ reflects additions
          ].filter(Boolean) as string[],
        ),
      ],
      profileImageFid: (payload.imagesFids ?? [])[0] || (payload.newImagesFids ?? [])[0],
    };

    const enhancedProductPayload = {
      ...enhancePayload,
      titleAr: payload?.arabicName ?? undefined,
      titleEn: payload?.englishName || null,
      mfgCompanyId: payload?.mfgCompany,
    };

    const enhanceSubProductPayload = {
      ...enhancePayload,
      attributes: { productAttributeValueIds: this.selectedAttributeValueIds() },
    };

    // console.log('sub product payload: ', enhanceSubProductPayload);
    this.loading.set(true);

    console.log('enhanced payload: ', enhancedProductPayload);
    if (this.productType() === 'sub-product') {
      this.productService
        .updateSubProduct(payload.id!, payload.subId!, enhanceSubProductPayload)
        .pipe(
          finalize(() => {
            this.loading.set(false);
          }),
        )
        .subscribe({
          next: () => {
            this.successUpdate.set(true);
            setTimeout(() => {
              this.router.navigate(['/admin/products/view-sub-product', payload.id, payload.subId]);
            }, 600);
          },
          error: (err) => {
            // throw err;
            this.errorUpdaingProduct.set(
              this.isArabic
                ? err.messageAr || err.messageEn || 'حدث بعض الخطأ!'
                : err.messageEn || err.messageAr || 'some error occured!',
            );
          },
        });
    } else {
      this.productService
        .updateProduct(payload.id!, enhancedProductPayload)
        .pipe(
          finalize(() => {
            this.loading.set(false);
          }),
        )
        .subscribe({
          next: () => {
            this.successUpdate.set(true);
            setTimeout(() => {
              this.router.navigate(['/admin/products/view-product', payload.id]);
            }, 600);
          },
          error: (err) => {
            // throw err;
            this.errorUpdaingProduct.set(
              this.isArabic
                ? err.messageAr || err.messageEn || 'حدث بعض الخطأ!'
                : err.messageEn || err.messageAr || 'some error occured!',
            );
          },
        });
    }
  }

  onCancelConfirmed() {
    this.confirmationCancel.set(false);
  }

  openConfirmationCancel() {
    this.confirmationCancel.set(true);
  }
  private calcSellerPrice(): void {
    const people = this.productForm.get('peoplePrice')!.value;
    const discount = this.productForm.get('sellerDiscount')!.value;
    const result =
      people !== null && people > 0 && discount !== null ? (people - (people * discount) / 100).toFixed(2) : '';
    this.productForm.get('sellerPrice')!.setValue(+result);
  }

  private calcPharmacyPrice(): void {
    const people = this.productForm.get('peoplePrice')!.value;
    const discount = this.productForm.get('pharmacyDiscount')!.value;
    const result =
      people != null && people > 0 && discount != null ? (people - (people * discount) / 100).toFixed(2) : '';
    this.productForm.get('pharmacyPrice')!.setValue(+result);
  }

  blockInvalidNumberChars(event: KeyboardEvent): void {
    // e = scientific notation, + and - = sign chars, all invalid for discount fields
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  setPrimaryImage(fid: string) {
    const currentFids = this.productForm.get('imagesFids')?.value ?? [];
    const reordered = [fid, ...currentFids.filter((f) => f !== fid)];
    this.productForm.patchValue({ imagesFids: reordered });

    // Reorder the display: move the clicked image to productImageUrl,
    // and push the old primary to productImages
    const oldPrimary = this.productImageUrl();
    const newPrimary = this.productImages().find((img) => img.fid === fid);

    if (newPrimary && oldPrimary?.url) {
      // Add old primary to the start of extra images
      this.productImages.update((imgs) => [
        { fid: currentFids[0], url: oldPrimary.url, isImage: oldPrimary.isImage, loading: false, error: false },
        ...imgs.filter((img) => img.fid !== fid),
      ]);
    }

    this.productImageUrl.set({ url: newPrimary!.url, isImage: newPrimary!.isImage });
  }
  get isArabic() {
    return this.locale.startsWith('ar');
  }
  get isEnglish() {
    return this.locale.startsWith('en');
  }
}
